import { Stack } from "@mantine/core"
import { CountryIso3166 } from "@tfg/db"
import {
  CountrySelectorCustomRender,
  FilterBuilder,
  FilterBuilderProps,
  FilterOptions,
} from "@tfg/ui"
import _ from "lodash"
import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import { removeUndefined } from "@tfg/types/schemas/utils"

import {
  useFindProductBrandValues,
  useFindProductCollectionValues,
  useFindProductSupplierContactValues,
  useFindProductTypeValues,
  ProductFilterAndSearchRequestDto,
} from "~/gen/api"
import { COUNTRIES } from "~/utils/countries"

interface Props<T extends ProductFilterAndSearchRequestDto> {
  setFiltersValue: Dispatch<SetStateAction<T>>
  filtersValue: T
  extraFilters?: FilterBuilderProps<T>["options"]
}

export const ProductFilters = <T extends ProductFilterAndSearchRequestDto>({
  setFiltersValue,
  filtersValue,
  extraFilters,
}: Props<T>) => {
  const { t } = useTranslation("products")
  const { data: productTypeValues } = useFindProductTypeValues(
    removeUndefined({ ...filtersValue, type: undefined })
  )
  const { data: productCollectionValues } = useFindProductCollectionValues(
    removeUndefined({ ...filtersValue, collection: undefined })
  )
  const { data: productBrandValues } = useFindProductBrandValues(
    removeUndefined({ ...filtersValue, brand: undefined })
  )
  const { data: supplierContactValues } = useFindProductSupplierContactValues(
    removeUndefined({
      ...filtersValue,
      organizationId: undefined,
      country: undefined,
    })
  )

  const organizations = _.uniqBy(
    supplierContactValues?.data.map((supplier) => supplier.organization),
    "id"
  )

  const countries = _.uniq(
    supplierContactValues?.data.map((supplier) => supplier.organization.country)
  )

  const options: FilterOptions<T>[] = [
    {
      label: t("lists.placeholders.type"),
      key: "type",
      type: "select",
      options:
        productTypeValues?.data.map((type) => ({
          label: type,
          value: type as T["type"],
        })) ?? [],
    },
    {
      label: t("lists.placeholders.collection"),
      key: "collection",
      type: "select",
      options:
        productCollectionValues?.data.map((collection) => ({
          label: collection,
          value: collection as T["collection"],
        })) ?? [],
    },
    {
      label: t("lists.placeholders.companies"),
      key: "organizationId",
      options: organizations.map((organization) => ({
        label: organization.name,
        value: organization.id as T["organizationId"],
      })),
      type: "select",
    },
    {
      label: t("lists.placeholders.country"),
      key: "country",
      options: Object.entries(COUNTRIES)
        .map(([_, value]) => ({
          label: value.countryName,
          value: value.code as T["country"],
        }))
        .filter((country) =>
          countries.includes(country.value as CountryIso3166)
        ),
      customRender: CountrySelectorCustomRender(COUNTRIES),
      type: "select",
    },
  ]

  if (productBrandValues?.data && productBrandValues?.data?.length > 0) {
    options.push({
      label: t("lists.placeholders.brand"),
      key: "brand",
      options: productBrandValues.data.map((brand) => ({
        label: brand,
        value: brand as T["brand"],
      })),
      type: "select",
    })
  }

  return (
    <Stack>
      <FilterBuilder
        title={t("filters.title")}
        searchPlaceholder={t("filters.search.placeholder")}
        filters={filtersValue}
        setFilters={setFiltersValue}
        options={[...options, ...(extraFilters ?? [])]}
      />
    </Stack>
  )
}
